/**
 * Character Count Service for SMS Messages with Dynamic Variables
 *
 * Provides accurate character counting by expanding dynamic variables
 * to their actual lengths based on restaurant data.
 */

import { supabase } from '@/integrations/supabase/client';

export interface VariableLength {
  min: number;
  max: number;
  average: number;
}

export interface CharacterStats {
  original: number;          // Original message length with placeholders
  minimum: number;           // Minimum possible length with expanded variables
  maximum: number;           // Maximum possible length with expanded variables
  average: number;           // Average expected length
  segments: {
    min: number;            // Minimum SMS segments needed
    max: number;            // Maximum SMS segments needed
    avg: number;            // Average SMS segments expected
  };
  hasVariables: boolean;     // Whether message contains dynamic variables
  hasUnicode: boolean;       // Whether message contains unicode characters
}

const SMS_CHAR_LIMIT = 160;
const SMS_CHAR_LIMIT_UNICODE = 70;
const SMS_CHAR_LIMIT_MULTIPART = 153;
const SMS_CHAR_LIMIT_MULTIPART_UNICODE = 67;

/**
 * Standard opt-out message that's appended to all SMS
 *
 * KUDOSITY SYSTEM (Current):
 * - We send: '\n\n[unsub-reply-link]' (20 chars)
 * - Kudosity replaces [unsub-reply-link] (18 chars) with UnsubRep.ly/xxxxxxxx (20 chars)
 * - Customer receives: 22 chars total (2 newlines + 20 char link)
 *
 * LEGACY SYSTEM (Previous):
 * - We send and customer receives: '\n\nReply STOP to opt-out' (23 chars)
 *
 * NOTE: This constant represents what we SEND (20 chars).
 * Use OPT_OUT_RECEIVED_LENGTH for what customers actually receive (22 chars).
 */
export const OPT_OUT_MESSAGE = '\n\n[unsub-reply-link]';

/**
 * The actual character count that customers receive
 * Accounts for Kudosity's dynamic link replacement
 *
 * - We send: [unsub-reply-link] (18 chars)
 * - Kudosity replaces with: UnsubRep.ly/xxxxxxxx (20 chars)
 * - Total received: 2 newlines + 20 char link = 22 characters
 */
export const OPT_OUT_RECEIVED_LENGTH = 22;

export class CharacterCountService {
  private variableLengths: Map<string, VariableLength> = new Map();
  private organisationId: string;

  constructor(organisationId?: string) {
    this.organisationId = organisationId || '';
    this.setDefaultVariableLengths();
  }

  /**
   * Set default estimated lengths for common variables
   */
  private setDefaultVariableLengths() {
    // Customer fields
    // First name: 3-9 chars (only 0.68% of 16,070+ customers exceed 9 chars)
    this.variableLengths.set('{first name}', { min: 3, max: 9, average: 5 });
    this.variableLengths.set('{last name}', { min: 3, max: 15, average: 6 });
    this.variableLengths.set('{email}', { min: 10, max: 33, average: 22 });

    // Default URL estimates if no restaurant data available
    this.variableLengths.set('{preferred restaurant ordering url}', { min: 20, max: 32, average: 25 });
    this.variableLengths.set('{preferred restaurant review url}', { min: 22, max: 40, average: 25 });
    this.variableLengths.set('{preferred restaurant google review url}', { min: 30, max: 32, average: 31 });
    this.variableLengths.set('{preferred restaurant instagram url}', { min: 23, max: 45, average: 30 });
    this.variableLengths.set('{preferred restaurant facebook url}', { min: 25, max: 52, average: 30 });
    this.variableLengths.set('{preferred restaurant name}', { min: 6, max: 22, average: 12 });
  }

  /**
   * Fetch actual variable lengths from restaurant data
   */
  async fetchVariableLengths(organisationId: string) {
    if (!organisationId) return;

    this.organisationId = organisationId;

    try {
      const { data: restaurants, error } = await supabase
        .from('restaurants_master')
        .select(`
          ordering_page_url,
          review_page_url,
          template_restaurant_name,
          google_review_url,
          instagram_url,
          facebook_url
        `)
        .eq('organisation_id', organisationId);

      if (error || !restaurants || restaurants.length === 0) {
        console.warn('Could not fetch restaurant data for character count:', error);
        return;
      }

      // Calculate actual min/max/avg for URLs
      this.calculateFieldLengths(restaurants, 'ordering_page_url', '{preferred restaurant ordering url}');
      this.calculateFieldLengths(restaurants, 'review_page_url', '{preferred restaurant review url}');
      this.calculateFieldLengths(restaurants, 'google_review_url', '{preferred restaurant google review url}');
      this.calculateFieldLengths(restaurants, 'instagram_url', '{preferred restaurant instagram url}');
      this.calculateFieldLengths(restaurants, 'facebook_url', '{preferred restaurant facebook url}');
      this.calculateFieldLengths(restaurants, 'template_restaurant_name', '{preferred restaurant name}');

    } catch (error) {
      console.error('Error fetching restaurant data for character count:', error);
    }
  }

  /**
   * Calculate min/max/average lengths for a specific field
   */
  private calculateFieldLengths(restaurants: any[], field: string, variable: string) {
    const values = restaurants
      .map(r => r[field])
      .filter(v => v != null && v !== '')
      .map(v => v.length);

    if (values.length === 0) return;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const sum = values.reduce((a, b) => a + b, 0);
    const average = Math.round(sum / values.length);

    this.variableLengths.set(variable, { min, max, average });
  }

  /**
   * Check if text contains unicode characters
   */
  private containsUnicode(text: string): boolean {
    for (let i = 0; i < text.length; i++) {
      if (text.charCodeAt(i) > 127) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calculate number of SMS segments needed
   */
  private calculateSegments(text: string): number {
    const length = text.length;
    const hasUnicode = this.containsUnicode(text);

    if (length === 0) return 1;

    // Single segment limits
    if (hasUnicode) {
      if (length <= SMS_CHAR_LIMIT_UNICODE) return 1;
      // Multi-part unicode messages
      return Math.ceil(length / SMS_CHAR_LIMIT_MULTIPART_UNICODE);
    } else {
      if (length <= SMS_CHAR_LIMIT) return 1;
      // Multi-part regular messages
      return Math.ceil(length / SMS_CHAR_LIMIT_MULTIPART);
    }
  }

  /**
   * Escape special regex characters
   */
  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Expand message with variable lengths
   */
  expandMessage(message: string, mode: 'min' | 'max' | 'average' = 'max'): string {
    let expanded = message;

    // Sort by length descending to replace longer variables first
    // This prevents partial replacements
    const sortedVariables = Array.from(this.variableLengths.entries())
      .sort((a, b) => b[0].length - a[0].length);

    sortedVariables.forEach(([variable, lengths]) => {
      const length = lengths[mode];
      const replacement = 'X'.repeat(length);
      const regex = new RegExp(this.escapeRegExp(variable), 'gi');
      expanded = expanded.replace(regex, replacement);
    });

    return expanded;
  }

  /**
   * Check if message contains any dynamic variables
   */
  private hasVariables(message: string): boolean {
    for (const [variable] of this.variableLengths) {
      if (message.toLowerCase().includes(variable.toLowerCase())) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calculate comprehensive character statistics
   *
   * NOTE: For character counting, we use the RECEIVED opt-out length (22 chars),
   * not the sent length (20 chars), since that's what customers see and what
   * affects billing/segmentation.
   */
  calculateStats(message: string, includeOptOut: boolean = true): CharacterStats {
    // Use a placeholder of the correct RECEIVED length for accurate calculations
    // Kudosity replaces [unsub-reply-link] (18 chars) with a 20-char link,
    // so customers receive 22 chars total (2 newlines + 20 char link)
    const optOutText = includeOptOut ? 'X'.repeat(OPT_OUT_RECEIVED_LENGTH) : '';
    const baseMessage = message + optOutText;

    // Check if message contains variables
    const hasVariables = this.hasVariables(message);

    if (!hasVariables) {
      // Simple calculation for messages without variables
      const length = baseMessage.length;
      const hasUnicode = this.containsUnicode(baseMessage);
      const segments = this.calculateSegments(baseMessage);

      return {
        original: message.length,
        minimum: length,
        maximum: length,
        average: length,
        segments: { min: segments, max: segments, avg: segments },
        hasVariables: false,
        hasUnicode
      };
    }

    // Expand message with different length modes
    const minExpanded = this.expandMessage(message, 'min') + optOutText;
    const maxExpanded = this.expandMessage(message, 'max') + optOutText;
    const avgExpanded = this.expandMessage(message, 'average') + optOutText;

    return {
      original: message.length,
      minimum: minExpanded.length,
      maximum: maxExpanded.length,
      average: avgExpanded.length,
      segments: {
        min: this.calculateSegments(minExpanded),
        max: this.calculateSegments(maxExpanded),
        avg: this.calculateSegments(avgExpanded)
      },
      hasVariables: true,
      hasUnicode: this.containsUnicode(maxExpanded) // Check unicode on worst case
    };
  }

  /**
   * Get a list of variables found in the message
   */
  getVariablesInMessage(message: string): string[] {
    const found: string[] = [];

    for (const [variable] of this.variableLengths) {
      if (message.toLowerCase().includes(variable.toLowerCase())) {
        found.push(variable);
      }
    }

    return found;
  }

  /**
   * Get the character limit for the message type
   */
  getCharacterLimit(hasUnicode: boolean, segments: number): number {
    if (segments === 1) {
      return hasUnicode ? SMS_CHAR_LIMIT_UNICODE : SMS_CHAR_LIMIT;
    } else {
      return hasUnicode ? SMS_CHAR_LIMIT_MULTIPART_UNICODE : SMS_CHAR_LIMIT_MULTIPART;
    }
  }
}

// Export singleton for convenience
let instance: CharacterCountService | null = null;

export function getCharacterCountService(organisationId?: string): CharacterCountService {
  if (!instance || (organisationId && instance['organisationId'] !== organisationId)) {
    instance = new CharacterCountService(organisationId);
  }
  return instance;
}